import {
  createFileRoute,
  type MakeRouteMatchUnion,
} from "@tanstack/react-router";
import {
  Loader,
  AppError,
  EmptyState,
  Button,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "@bfc-software/pallet";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  AuthContext,
  AuthUtils,
  isDefined,
  logger,
  useAssertedTenancyContext,
} from "@bfc-software/app-toolbox";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";
import { UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import {
  DetailField,
  RequirementsList,
  type RequirementLabels,
} from "../../-components/task-requirements";

import { PlanSummary } from "../../-components/plan-summary";
import { ActivationView } from "../../-components/types";
import { LoadLocationCard } from "../../-components/load-location-card";
import { DestinationNodeCard } from "../../-components/destination-node-card";
import sharedStyles from "../../-components/shared.module.css";

type TaskListResponse =
  TaskPlannerSchemaTypes.components["schemas"]["TaskListResponse"];
type TaskSummary = TaskPlannerSchemaTypes.components["schemas"]["TaskSummary"];
type TaskPlanSummaryResponse =
  TaskPlannerSchemaTypes.components["schemas"]["TaskPlanDetailResponse"];
type TaskDetailPickResponse =
  TaskPlannerSchemaTypes.components["schemas"]["TaskDetailPickResponse"];
type TaskDetailPick =
  TaskPlannerSchemaTypes.components["schemas"]["TaskDetailPick"];
type TaskDetailRequirementRecord =
  TaskPlannerSchemaTypes.components["schemas"]["TaskDetailRequirementRecord"];
type TaskActivationSummary =
  TaskPlannerSchemaTypes.components["schemas"]["TaskActivationSummary"];

/**
 * Requirements grouped by the pick line they prove (task_detail_pick.id), each
 * list ordered by `seq`. Empty until the plan has been activated, since
 * requirements are synthesized during activation.
 */
type RequirementsByPick = Map<string, TaskDetailRequirementRecord[]>;

const NO_REQUIREMENTS: TaskDetailRequirementRecord[] = [];

/**
 * Who this task is assigned to, derived from its task_activation row. There is
 * no assignment until the plan has been activated and a selector seeded, so an
 * un-activated plan (or an activation with no selector) reads as unassigned.
 */
type AssignmentView =
  | { kind: "loading" }
  | { kind: "unassigned" }
  | {
      kind: "assigned";
      /** `selector_profile.id` - the ULID, kept for the `title` and as a fallback. */
      selectorId: string;
      /**
       * The holder's AAA user id, which is what the badge actually says. Null is
       * possible on the wire only when `selectorId` is too, so an `assigned` view
       * should always carry it - but the type keeps the wire's nullability rather
       * than asserting a join we did not make.
       */
      externalUserId: string | null;
    };

type RoutePathState = {
  taskId: number;
  taskPlanId: string;
  org: string;
  location: string;
  lang: string;
};

type State =
  | { kind: "not-loaded"; route: RoutePathState }
  | { kind: "loading"; route: RoutePathState }
  | { kind: "failed"; route: RoutePathState }
  | { kind: "empty"; route: RoutePathState }
  | {
      kind: "loaded";
      picks: TaskDetailPick[];
      route: RoutePathState;
      tasks: Array<TaskSummary>;
      nextTask?: TaskSummary;
      previousTask?: TaskSummary;
      expandedPicks: Set<string>;
      planSummary: TaskPlanSummaryResponse;
    };

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/task-plans/$task-plan/$task/",
)({
  staticData: {
    crumb: (match: MakeRouteMatchUnion, t: TFunction) => {
      const params = match.params as {
        lang: string;
        org: string;
        location: string;
        "task-plan": string;
        task: string;
      };
      const base = {
        lang: params.lang,
        org: params.org,
        location: params.location,
      };

      return [
        {
          label: t("page.taskPlans.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/task-plans",
          params: base,
        },
        {
          label: t("page.taskPlanDetail.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan",
          params: { ...base, "task-plan": params["task-plan"] },
        },
        {
          label: t("page.taskDetail.crumb"),
        },
      ];
    },
  },
  component: RouteComponent,
});

const COLUMN_COUNT = 6;

/**
 * The per-variant requirement rendering (`RequirementsList` and friends) moved
 * to `task-plans/-components/task-requirements` (SR-318) so the outbound task detail
 * drawer can reuse it rather than growing a second renderer of the same
 * six-variant union. This page's `warehouse.json` keys stay exactly where
 * they were - `requirementLabels` below is just the adapter from this page's
 * `t` to the shared component's typed props.
 */
function requirementLabels(t: TFunction): RequirementLabels {
  return {
    heading: t("page.taskDetail.requirements.heading"),
    kind: {
      scan_slot_code: t("page.taskDetail.requirements.scanSlotCode"),
      scan_item_code: t("page.taskDetail.requirements.scanItemCode"),
      confirm_count: t("page.taskDetail.requirements.confirmCount"),
      confirm_catchweight: t("page.taskDetail.requirements.confirmCatchweight"),
      confirm_lot: t("page.taskDetail.requirements.confirmLot"),
      confirm_expiry: t("page.taskDetail.requirements.confirmExpiry"),
    },
    slotId: t("page.taskDetail.requirements.slotId"),
    slotUpc: t("page.taskDetail.requirements.slotUpc"),
    itemId: t("page.taskDetail.requirements.itemId"),
    itemUpc: t("page.taskDetail.requirements.itemUpc"),
    expectedQuantity: t("page.taskDetail.requirements.expectedQuantity"),
    weightMin: t("page.taskDetail.requirements.weightMin"),
    weightMax: t("page.taskDetail.requirements.weightMax"),
    weightUom: t("page.taskDetail.requirements.weightUom"),
    expiryNotBefore: t("page.taskDetail.requirements.expiryNotBefore"),
    expiryNotAfter: t("page.taskDetail.requirements.expiryNotAfter"),
    exceptions: t("page.taskDetail.requirements.exceptions"),
    catchweightNote: t("page.taskDetail.requirements.catchweightNote"),
    confirmLotNote: t("page.taskDetail.requirements.confirmLotNote"),
    confirmExpiryNote: t("page.taskDetail.requirements.confirmExpiryNote"),
  };
}

/**
 * Renders who the task is assigned to next to the task navigation. Only shown
 * once we have a selector; while resolving or when unassigned it renders
 * nothing, so the toolbar stays quiet until there is something to say.
 *
 * What it says is the AAA user id, not the `selector_profile` ULID: this badge
 * names a person, and the ULID names a row. The ULID stays on the `title`, and is
 * the fallback for the case the wire says cannot happen.
 */
function AssignmentBadge({
  assignment,
  t,
}: {
  assignment: AssignmentView;
  t: TFunction;
}) {
  if (assignment.kind !== "assigned") {
    return null;
  }

  return (
    <span className={sharedStyles.assignment} data-test-role="task-assignment">
      <span className={sharedStyles.assignmentLabel}>
        {t("page.taskDetail.assignedTo")}
      </span>
      <span
        className={sharedStyles.assignmentValue}
        data-test-role="task-selector-id"
        title={assignment.selectorId}
      >
        {assignment.externalUserId ?? assignment.selectorId}
      </span>
    </span>
  );
}

function PickRow({
  pick,
  isExpanded,
  onToggle,
  requirements,
  t,
}: {
  pick: TaskDetailPick;
  isExpanded: boolean;
  onToggle: () => void;
  requirements: TaskDetailRequirementRecord[];
  t: TFunction;
}) {
  return (
    <>
      <TableRow data-test-role="task-detail-pick-row">
        <TableCell>{pick.detail_order}</TableCell>
        <TableCell>
          {/*
            The two numbers a pick has are both worth a column of their own and
            mean different things: `detail_order` is where the algorithm put this
            pick in the task, `line_number` where it came from in the input file.
            The expand control rides on the second, because the ULID it used to
            show is not something anyone reads - it stays on the `title` of the
            wrapper, since pallet's Button takes no `title` of its own.
          */}
          <span title={pick.id} className={sharedStyles.truncatedId}>
            <Button
              kind="text-only"
              variant="secondary"
              scale="inline"
              overflow="truncate"
              text={String(pick.line_number)}
              onPress={onToggle}
              data-test-role="task-detail-pick-toggle"
            />
          </span>
        </TableCell>
        <TableCell>{pick.item_id}</TableCell>
        <TableCell>{pick.lpn}</TableCell>
        <TableCell>{pick.quantity}</TableCell>
        <TableCell>{pick.product_type}</TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow data-test-role="task-detail-pick-expanded">
          <TableCell colSpan={COLUMN_COUNT}>
            <div className={sharedStyles.expandedDetail}>
              <div className={sharedStyles.expandedDetailTop}>
                <div className={sharedStyles.expandedFields}>
                  <DetailField
                    label={t("page.taskDetail.detail.itemName")}
                    value={pick.item_name}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.uom")}
                    value={pick.uom}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.location")}
                    value={pick.location}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.pickSequence")}
                    value={pick.pick_sequence}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.lpn")}
                    value={pick.lpn}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.cube")}
                    value={pick.cube}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.weight")}
                    value={pick.weight}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.order")}
                    value={pick.order_number}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.stopNumber")}
                    value={pick.stop_number}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.stopId")}
                    value={pick.stop_id}
                  />
                  <DetailField
                    label={t("page.taskDetail.detail.lineNumber")}
                    value={pick.line_number}
                  />
                </div>
                <div>
                  <LoadLocationCard loc={pick.load_location} t={t} />
                  <DestinationNodeCard node={pick.destination_node} t={t} />
                </div>
              </div>
              {requirements.length > 0 && (
                <RequirementsList
                  requirements={requirements}
                  labels={requirementLabels(t)}
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function PicksTable({
  children,
  t,
}: {
  children?: React.ReactNode;
  t: TFunction;
}) {
  return (
    <Table
      aria-label={t("page.taskDetail.table.ariaLabel")}
      isResizable={false}
    >
      <TableHead>
        <TableHeaderCell>{t("page.taskDetail.table.seq")}</TableHeaderCell>
        <TableHeaderCell isRowHeader={true}>
          {t("page.taskDetail.table.line")}
        </TableHeaderCell>
        <TableHeaderCell>{t("page.taskDetail.table.item")}</TableHeaderCell>
        <TableHeaderCell>{t("page.taskDetail.table.lpn")}</TableHeaderCell>
        <TableHeaderCell>{t("page.taskDetail.table.quantity")}</TableHeaderCell>
        <TableHeaderCell>{t("page.taskDetail.table.type")}</TableHeaderCell>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

function assertNever(x: never): never {
  throw new Error(`Unhandled case: ${JSON.stringify(x)}`);
}

function TaskDetailPage({
  state,
  activation,
  orgId,
  assignment,
  requirementsByPick,
  t,
  setState,
}: {
  state: State;
  orgId: string;
  activation: ActivationView;
  assignment: AssignmentView;
  requirementsByPick: RequirementsByPick;
  t: TFunction;
  setState: (s: State) => void;
}) {
  logger.trace(`Rendering task detail route state '${state.kind}'`);
  const params = Route.useParams();
  const navigate = useNavigate();

  switch (state.kind) {
    case "not-loaded":
    case "loading":
      return (
        <div
          data-test-role="task-detail-loading"
          className={sharedStyles.loadingContainer}
        >
          <Loader type="page" />
        </div>
      );
    case "empty":
      return (
        <PicksTable t={t}>
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <EmptyState heading={t("page.taskDetail.empty")} />
            </TableCell>
          </TableRow>
        </PicksTable>
      );
    case "loaded": {
      const togglePick = (id: string) => {
        const { expandedPicks } = state;

        if (expandedPicks.has(id)) {
          expandedPicks.delete(id);
        } else {
          expandedPicks.add(id);
        }

        setState({ ...state, expandedPicks });
      };

      const table = (
        <PicksTable t={t}>
          {state.picks.map((pick) => (
            <PickRow
              key={pick.id}
              pick={pick}
              isExpanded={state.expandedPicks.has(pick.id)}
              onToggle={() => togglePick(pick.id)}
              requirements={requirementsByPick.get(pick.id) ?? NO_REQUIREMENTS}
              t={t}
            />
          ))}
        </PicksTable>
      );

      const summary = (
        <PlanSummary
          plan={state.planSummary}
          orgId={orgId}
          activation={activation}
          t={t}
          lang={params.lang}
          location={params.location}
        />
      );

      const goToTask = (taskId: number) => {
        return () => {
          setState({
            kind: "not-loaded",
            route: { ...state.route, taskId },
          });

          navigate({
            to: "/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan/$task",
            params: {
              lang: params.lang,
              org: params.org,
              location: params.location,
              "task-plan": params["task-plan"],
              task: `${taskId}`,
            },
          });
        };
      };

      return (
        <div className={sharedStyles.planDetailLayout}>
          {summary}
          <div className={sharedStyles.planDetailMain}>
            <div className={sharedStyles.planDetailActions}>
              <AssignmentBadge assignment={assignment} t={t} />
              <div className={sharedStyles.planDetailActionsRight}>
                <div className={sharedStyles.planDetailAction}>
                  <Button
                    kind="text-only"
                    text={t("page.taskDetail.previousTask")}
                    data-test-role="previous-task-nav"
                    variant="secondary"
                    isDisabled={!isDefined(state.previousTask)}
                    onPress={goToTask(
                      state.previousTask?.task_internal_id ?? 0,
                    )}
                  />
                </div>
                <div className={sharedStyles.planDetailAction}>
                  <Button
                    kind="text-only"
                    text={t("page.taskDetail.nextTask")}
                    data-test-role="previous-task-nav"
                    variant="secondary"
                    isDisabled={!isDefined(state.nextTask)}
                    onPress={goToTask(state.nextTask?.task_internal_id ?? 0)}
                  />
                </div>
              </div>
            </div>
            <div>{table}</div>
          </div>
        </div>
      );
    }
    case "failed":
      return (
        <PicksTable t={t}>
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <div className={sharedStyles.errorContainer}>
                <AppError
                  description={t("page.taskDetail.failed")}
                  resolution={t("page.taskDetail.failureSupport")}
                />
              </div>
            </TableCell>
          </TableRow>
        </PicksTable>
      );
    default:
      return assertNever(state);
  }
}

function nextState<T>(
  state: State,
  detailsQuery: UseQueryResult<TaskDetailPickResponse, T>,
  planQuery: UseQueryResult<TaskPlanSummaryResponse, T>,
  tasks: UseQueryResult<TaskListResponse, T>,
): State | undefined {
  const { route } = state;

  switch (state.kind) {
    case "not-loaded":
      if (detailsQuery.isFetching) {
        return { kind: "loading", route };
      }
      break;
    case "loading": {
      const hasData =
        planQuery.isSuccess && detailsQuery.isSuccess && tasks.isSuccess;

      if (planQuery.isError || detailsQuery.isError || tasks.isError) {
        logger.warn("Failed query on task detail page", {
          planQuery: planQuery.isError,
          detailsQuery: detailsQuery.isError,
          tasks: tasks.isError,
        });
        return { kind: "failed", route };
      }

      if (!hasData) {
        return state;
      }

      const { task_detail_picks: picks } = detailsQuery.data;
      if (!Array.isArray(picks)) {
        return state;
      }

      if (picks.length === 0) {
        return { kind: "empty", route: state.route };
      }

      // Note: We are using the per-tenant incrementing task id here instead of the per-plan
      // incrementing 'sort_order' field. The `/task-plans/{planId}/{taskId}` route structure
      // should eventually use a consistent value between the `{planId}` and `{taskId}` path
      // params; right now the `{planId}` is a ULID and the `{taskId}` is the numerical, per-tenant
      // incrementing task id.
      const nextTask = tasks.data.tasks.find(
        (otherTask) => otherTask.task_internal_id === state.route.taskId + 1,
      );

      const previousTask = tasks.data.tasks.find(
        (otherTask) => otherTask.task_internal_id === state.route.taskId - 1,
      );

      return {
        kind: "loaded",
        tasks: tasks.data.tasks,
        picks,
        route: state.route,
        expandedPicks: new Set(),
        planSummary: planQuery.data,
        nextTask: isDefined(nextTask) ? nextTask : void 0,
        previousTask: isDefined(previousTask) ? previousTask : void 0,
      };
    }
  }

  return void 0;
}

export function RouteComponent() {
  const navigate = useNavigate();
  const {
    org,
    location,
    lang,
    "task-plan": planId,
    task: taskIdParam,
  } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const taskId = parseInt(taskIdParam, 10);

  const [state, setState] = useState<State>({
    kind: "not-loaded",
    route: {
      org,
      location,
      lang,
      taskPlanId: planId,
      taskId,
    },
  });

  const client = useTaskPlannerApi();

  const { orgId, warehouseId } = useAssertedTenancyContext();
  const tenantId = AuthUtils.getTenantId(authState, orgId, warehouseId);

  const planQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}",
    { params: { path: { orgId, id: planId } } },
    { enabled: !!tenantId },
  );

  const tasksQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/tasks",
    { params: { path: { orgId, id: planId } } },
    { enabled: !!tenantId },
  );

  const detailsQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/tasks/{taskId}/details",
    {
      params: {
        path: { orgId, id: planId, taskId },
      },
    },
    { enabled: !!tenantId },
  );

  // The plan detail already carries a handle to the most recent activation, so
  // we read that activation directly rather than listing activations first. A
  // null rollup means the plan has never been activated.
  //
  // `activation.id` rather than the `current_activation_id` beside it: the spec
  // deprecates that field in favour of the rollup, which carries the status too.
  const activationId = planQuery.data?.activation?.id ?? undefined;

  const activationQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/activations/{activationId}",
    {
      params: {
        path: { orgId, id: planId, activationId: activationId ?? "" },
      },
    },
    { enabled: !!tenantId && !!activationId },
  );

  // Requirements are synthesized during activation, so we only fetch them once
  // we know the plan has an activation. The endpoint is keyed by plan + the
  // warehouse-scoped task id (the same one in the route); each record self-
  // reports the pick line it proves.
  const requirementsQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/tasks/{taskId}/requirements",
    {
      params: {
        path: { orgId, id: planId, taskId },
      },
    },
    { enabled: !!tenantId && !!activationId },
  );

  // The route param is the warehouse-scoped task id; task activations are keyed
  // by the internal task ulid, which we resolve from the task list.
  const currentTaskUlid = tasksQuery.data?.tasks.find(
    (task) => task.task_internal_id === taskId,
  )?.id;

  // Fetch only this task's activation rows (the taskIds filter), rather
  // than sweeping the whole plan. Gated on an activation existing and us knowing
  // the internal task id.
  //
  // planActivationId here is a filter rather than a path segment, so an id that
  // resolves to nothing comes back as an empty page instead of the 404 the
  // retired plan-scoped read returned. The `enabled` gate below is what keeps us
  // from asking in the first place; an empty page reads as unassigned either way.
  const taskActivationsQuery = client.useQuery(
    "get",
    "/{orgId}/task-activations",
    {
      params: {
        path: { orgId },
        query: {
          planActivationId: activationId,
          // Required by the listing: the warehouse-scoped identifiers it filters
          // on are unique only within one warehouse, so there is no org-wide
          // read. `location` is the AAA locid the route is keyed on, which is
          // exactly what this param takes.
          warehouseExtId: warehouseId,
          taskIds: currentTaskUlid ? [currentTaskUlid] : undefined,
          // Supersede history included: the rule below picks the live row, and
          // the plan-scoped read this replaces was never live-filtered either.
          live: false,
        },
      },
    },
    { enabled: !!tenantId && !!activationId && !!currentTaskUlid },
  );

  const assignment = useMemo<AssignmentView>(() => {
    if (planQuery.isLoading || tasksQuery.isLoading) {
      return { kind: "loading" };
    }
    // No activation, or the task isn't in the list: nothing to assign.
    if (!activationId || !currentTaskUlid) {
      return { kind: "unassigned" };
    }
    if (taskActivationsQuery.isLoading) {
      return { kind: "loading" };
    }
    if (taskActivationsQuery.isError || !taskActivationsQuery.data) {
      return { kind: "unassigned" };
    }
    // The listing can include rows retired by the JIT supersede lifecycle; the
    // live row is the one not yet superseded (in the MVP there is only one).
    const rows = taskActivationsQuery.data.task_activations;
    const current: TaskActivationSummary | undefined =
      rows.find((row) => row.superseded_at == null) ?? rows[0];
    const selectorId = current?.selector_id;
    return selectorId
      ? {
          kind: "assigned",
          selectorId,
          externalUserId: current?.external_user_id ?? null,
        }
      : { kind: "unassigned" };
  }, [
    planQuery.isLoading,
    tasksQuery.isLoading,
    activationId,
    currentTaskUlid,
    taskActivationsQuery.isLoading,
    taskActivationsQuery.isError,
    taskActivationsQuery.data,
  ]);

  const activation = useMemo<ActivationView>(() => {
    if (planQuery.isLoading) {
      return { kind: "loading" };
    }
    if (!activationId) {
      return { kind: "none" };
    }
    if (activationQuery.data) {
      return {
        kind: "present",
        status: activationQuery.data.status,
        errors: activationQuery.data.errors ?? [],
      };
    }
    if (activationQuery.isError) {
      logger.warn("Failed to load activation detail on task detail page", {
        activationId,
      });
      return { kind: "none" };
    }
    return { kind: "loading" };
  }, [
    planQuery.isLoading,
    activationId,
    activationQuery.data,
    activationQuery.isError,
  ]);

  const requirementsByPick = useMemo<RequirementsByPick>(() => {
    const byPick: RequirementsByPick = new Map();
    const records = requirementsQuery.data?.requirements;
    if (!records) {
      return byPick;
    }
    for (const record of records) {
      const list = byPick.get(record.task_detail_pick_id) ?? [];
      list.push(record);
      byPick.set(record.task_detail_pick_id, list);
    }
    for (const list of byPick.values()) {
      list.sort((a, b) => a.seq - b.seq);
    }
    return byPick;
  }, [requirementsQuery.data]);

  useEffect(() => {
    if (Number.isNaN(taskId)) {
      logger.warn("Invalid task id used in task detail page url", {
        taskIdParam,
      });

      navigate({
        to: "/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan",
        params: {
          org,
          lang,
          location,
          "task-plan": planId,
        },
      });
      return;
    }

    const updatedState = nextState(state, detailsQuery, planQuery, tasksQuery);

    if (typeof updatedState !== "undefined") {
      setState(updatedState);
    }
  }, [
    detailsQuery,
    state,
    planQuery,
    tasksQuery,
    setState,
    navigate,
    lang,
    org,
    location,
    planId,
    taskId,
    taskIdParam,
  ]);

  return (
    <div
      className="bg-surface h-full w-full"
      data-org-id={orgId}
      data-tenant-id={tenantId}
    >
      <TaskDetailPage
        state={state}
        activation={activation}
        assignment={assignment}
        orgId={orgId}
        requirementsByPick={requirementsByPick}
        t={t}
        setState={setState}
      />
    </div>
  );
}
